import { useEffect, useMemo, useRef, useState } from 'react'
import axios from 'axios'
import { Alert, Button, Collapse, Loader, Paper, Text } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import styled from 'styled-components'
import { FaFile, FaFolder, FaBackward, FaMusic, FaPlay, FaVolumeHigh } from 'react-icons/fa6'

import FZExclamation from '../FZExclamation'

import useMobile from 'src/hooks/useMobile'

const CLOUDFRONT_PREFIX = 'https://d32tv5vz8ua1sf.cloudfront.net/resources/korean/sounds/'
export const K1_URL = 'https://d32tv5vz8ua1sf.cloudfront.net/resources/korean/sounds/kfz1/KFZ_Book_1_Sound_Pack_Edition_3.1.zip'
export const K2_URL = 'https://d32tv5vz8ua1sf.cloudfront.net/resources/korean/sounds/kfz2/KFZ_Book_2_Sound_Pack_Edition_1.0.zip'
export const K3_URL = 'https://d32tv5vz8ua1sf.cloudfront.net/resources/korean/sounds/kfz3/KFZ_Book_3_Sound_Pack_Edition_1.0.zip'

const ResourcesWrapper = styled.div`
  
`

const ButtonContainer = styled.div<{ isMobile: boolean }>`
  display: flex;
  justify-content: center;
  gap: 30px;
  margin-top: ${({ isMobile }) => isMobile ? '0px' : '20px'};

  ${({ isMobile }) => isMobile && `
    flex-direction: column;
    gap: 20px;
    padding: 40px;
  `}
`

const ButtonSubContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;

  h2 {
    font-family: 'Milliard Bold';
  }

  button {
    font-family: 'Milliard Book';
  }
`

const COURSE_MAP = {
  kfz1: 'Korean From Zero! 1',
  kfz2: 'Korean From Zero! 2',
  kfz3: 'Korean From Zero! 3',
  korean1: 'Korean From Zero! 1',
  korean2: 'Korean From Zero! 2',
  korean3: 'Korean From Zero! 3'
}

const useFileManifest = (url: string) => {
  const [files, setFiles] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    async function fetchManifest() {
      try {
        setLoading(true)
        const response = await axios.get(`${CLOUDFRONT_PREFIX}${url}/manifest.json`)
        if (!response.data) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }
        setFiles(response.data?.files || [])
      } catch (err) {
        setError(err)
      } finally {
        setLoading(false)
      }
    }

    fetchManifest()
  }, [url])

  return { files, loading, error }
}

const getDirectoryItems = (keys: string[], currentPath: string) => {
  const subdirectories = new Set<string>()
  const files = new Set<string>()

  const prefix = currentPath ? currentPath.replace(/\/?$/, '/') : ''

  keys.forEach((key) => {
    if (prefix && !key.startsWith(prefix)) {
      if (currentPath === '' && key.includes('/')) {
        // still check if there's no subfolder
      }
      if (currentPath !== '') return
    }

    const remainder = key.slice(prefix.length)

    const slashIndex = remainder.indexOf('/')

    if (slashIndex === -1) {
      if (remainder && remainder.includes('.')) {
        files.add(remainder)
      }
    } else {
      const dirName = remainder.substring(0, slashIndex)
      if (dirName) {
        subdirectories.add(dirName)
      }
    }
  })

  return {
    subdirectories: Array.from(subdirectories).sort(),
    files: Array.from(files).sort()
  }
}

const CDNEntryDiv = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  padding: 15px;

  &:hover {
    background-color: #f5f5f5;
  }
`

// turn a dir name of the form lesson_01 into "Lesson 1", or "lesson_(A)" into Lesson A
const cleanDirName = (dirName: string) => {
  const match = dirName.match(/lesson_\(?(\d+|\w)\)?/i)
  if (match) {
    return `Lesson ${match[1]}`
  }

  return dirName.split('_').map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join(' ')
}

export const CDNFileBrowser = ({ url, onClose }) => {
  const { files: keys, loading, error } = useFileManifest(url)
  const [pathSegments, setPathSegments] = useState<string[]>([])
  const [audioPlaying, setAudioPlaying] = useState(false)
  const [audioFile, setAudioFile] = useState('')
  const [audioObject, setAudioObject] = useState(null)
  const audioRef = useRef(audioObject)
  const isMobile = useMobile()

  useEffect(() => {
    audioRef.current = audioObject
    if (audioRef.current) {
      audioRef.current.addEventListener('ended', () => {
        setAudioPlaying(false)
      })
      audioRef.current.play()
    }
  }, [audioObject])
  
  const currentPath = useMemo(() => pathSegments.join('/'), [pathSegments])

  const { subdirectories, files } = useMemo(() => {
    return getDirectoryItems(keys, currentPath)
  }, [keys, currentPath])

  const handleDirectoryClick = (dirName: string) => {
    setPathSegments((prev) => [...prev, dirName])
  }

  const handleFileClick = (fileName: string) => {
    const prefix = currentPath ? `${currentPath}/` : ''
    const fullKey = prefix + fileName
    const fileUrl = `${CLOUDFRONT_PREFIX}${url}/${fullKey}`
    window.open(fileUrl, '_blank', 'noopener,noreferrer')
  }

  const handleFilePlay = (e, fileName: string) => {
    e.preventDefault()
    e.stopPropagation()
    const prefix = currentPath ? `${currentPath}/` : ''
    const fullKey = prefix + fileName
    const fileUrl = `${CLOUDFRONT_PREFIX}${url}/${fullKey}`

    setAudioFile(fileUrl)
    setAudioPlaying(true)
    setAudioObject(new Audio(fileUrl))
  }

  const handleBack = () => {
    if (pathSegments.length === 0) {
      onClose()
    } else {
      setPathSegments((prev) => prev.slice(0, -1))
    }
  }

  if (loading) {
    return <Loader />
  }

  return (
    <div style={{ padding: 16, position: 'relative' }}>
      <FZExclamation size='small' title={`${COURSE_MAP[url]} Sounds`} color='blue' style={{ justifyContent: 'center' }} />

      {pathSegments.length > 0 ? (
        <Text size='sm' fw='bold' ta='center'>
          Current path: {pathSegments.map(cleanDirName).join(' > ')}
        </Text>
      ) : (
        <Text size='sm' fw='bold' ta='center'>
          Root directory
        </Text>
      )}

      <Button color='gray' onClick={handleBack} mb='md' style={{
        position: 'absolute', top: isMobile ? 0 : 20, left: 20
      }}>
        <FaBackward style={{ marginRight: 8 }} />
        Back
      </Button>
      <Paper shadow="xs" p="md" mt="md">
        {subdirectories.length === 0 ? (
          <></>
        ) : (
          <ul>
            {subdirectories.map((dir) => (
              <li key={dir} style={{ listStyle: 'none' }}>
                <CDNEntryDiv
                  onClick={() => handleDirectoryClick(dir)}
                >
                  <FaFolder style={{ marginRight: 10 }} />{cleanDirName(dir)}
                </CDNEntryDiv>
              </li>
            ))}
          </ul>
        )}

        {files.length === 0 ? (
          <></>
        ) : (
          <ul>
            {files.map((filename) => (
              <li key={filename} style={{ listStyle: 'none' }}>
                <CDNEntryDiv
                  onClick={(e) => handleFilePlay(e, filename)}
                >
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <FaMusic style={{ marginRight: 10 }} />
                    {filename}
                  </div>

                  {/* play button and download button at the end if .mp3 */}
                  <div style={{ marginLeft: 'auto' }}>
                    <Button
                      component="a"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleFileClick(filename)
                      }}
                      color='gray'
                    >
                      Download
                    </Button>
                    <Button
                      color='blue'
                      style={{ marginLeft: 10 }}
                      onClick={(e) => handleFilePlay(e, filename)}
                    >
                      {audioPlaying && audioFile === `${CLOUDFRONT_PREFIX}${url}/${currentPath}/${filename}` ? <FaVolumeHigh /> : <FaPlay />}
                    </Button>
                  </div>
                </CDNEntryDiv>
              </li>
            ))}
          </ul>
        )}
      </Paper>
    </div>
  )
}

const buildFileTree = (keys, basePrefix = 'resources/korean/sounds') => {
  const tree = {}

  for (const key of keys) {
    const parts = key.split('/')

    let currentLevel = tree
    parts.forEach((part, index) => {
      const isFile = part.includes('.')
      const isLast = index === parts.length - 1

      if (!currentLevel[part]) {
        currentLevel[part] = isLast && isFile ? { __file: true } : {}
      }
      currentLevel = currentLevel[part]
    })
  }

  return tree
}

const FileNode = ({ name, node, path }) => {
  const [opened, { toggle }] = useDisclosure(false)
  const isFile = node.__isFile

  const fileUrl = `${CLOUDFRONT_PREFIX}${path}`

  if (isFile) {
    return (
      <div style={{ marginLeft: 24, display: 'flex', alignItems: 'center' }}>
        <FaFile style={{ marginRight: 8 }} />
        <a href={fileUrl} target='_blank' rel='noreferrer'>{name}</a>
      </div>
    )
  }

  const childrenKeys = Object.keys(node).filter((k) => k !== '__isFile').sort()

  return (
    <div style={{ marginLeft: 16 }}>
      <div
        style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
        onClick={toggle}
      >
        <FaFolder style={{ marginRight: 8 }} />
        <Text>{name}</Text>
      </div>
      <Collapse in={opened}>
        {childrenKeys.map((key) => (
          <FileNode key={key} name={key} node={node[key]} path={`${path}/${key}`} />
        ))}
      </Collapse>
    </div>
  )
}

const FileBrowser = ({ tree }) => {
  const topLevelFolders = Object.keys(tree).sort()

  return (
    <div>
      {topLevelFolders.map((folderKey) => (
        <FileNode key={folderKey} name={folderKey} node={tree[folderKey]} path={folderKey} />
      ))}
    </div>
  )
}

const KoreanCourseFileBrowser = ({ url }) => {
  const { files, loading, error } = useFileManifest(url)

  const fileTree = useMemo(() => {
    if (files.length === 0) {
      return null
    }

    return buildFileTree(files)
  }, [files])

  if (loading) {
    return <Loader />
  }

  if (error) {
    return <Alert title="Error" color="red">{error.message}</Alert>
  }

  return (
    <>
      {!loading && !error && fileTree && <FileBrowser tree={fileTree} />}
    </>
  )
}

const KoreanResourcesPage = () => {
  const [activeCourse, setActiveCourse] = useState('')
  const isMobile = useMobile()

  if (activeCourse) {
    return (
      <>
        {/* <KoreanCourseFileBrowser url={activeCourse} /> */}
        <CDNFileBrowser url={activeCourse} onClose={() => setActiveCourse('')} />
      </>
    )
  }

  return (
    <ResourcesWrapper>
      <FZExclamation
        title='Korean From Zero! Resources'
        size='small'
        color='blue'
        style={{ justifyContent: 'center', fontSize: '1.4rem' }}
      />
      <Paper shadow="xs" p="md">
        <Text ta='center' style={{ fontFamily: 'Milliard Book' }}>
          Click any course&apos;s &quot;Desktop/Mobile&quot; button to view sounds individually, or &quot;Zip File&quot; to download the entire course&apos;s sound pack.
        </Text>
      </Paper>
      <ButtonContainer isMobile={isMobile}>
        <ButtonSubContainer>
          <h2>Book 1 Sounds</h2>
          <Button color='green' onClick={() => setActiveCourse('kfz1')}>Desktop/Mobile</Button>
          <Button component="a" href={K1_URL} target="_blank" download color='gray'>Zip File</Button>
        </ButtonSubContainer>
        <ButtonSubContainer>
          <h2>Book 2 Sounds</h2>
          <Button color='green' onClick={() => setActiveCourse('kfz2')}>Desktop/Mobile</Button>
          <Button component="a" href={K2_URL} target="_blank" download color='gray'>Zip File</Button>
        </ButtonSubContainer>
        <ButtonSubContainer>
          <h2>Book 3 Sounds</h2>
          <Button color='green' onClick={() => setActiveCourse('kfz3')}>Desktop/Mobile</Button>
          <Button component="a" href={K3_URL} target="_blank" download color='gray'>Zip File</Button>
        </ButtonSubContainer>
     </ButtonContainer>
    </ResourcesWrapper>
  )
}

export default KoreanResourcesPage
